import { useEffect, useLayoutEffect, useRef, useState } from "react";

const SCROLL_DELTA_HIDE = 14;
const SCROLL_DELTA_SHOW = 8;
const TOP_REVEAL_Y = 32;
const AUTH_TOOLBAR_HEIGHT = "3.5rem";
/** Matches CSS transition on --auth-toolbar-height; prevents scroll feedback during animation. */
const TOGGLE_COOLDOWN_MS = 520;

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function applySiteHeaderVisible(show) {
  const root = document.documentElement;
  const toolbar = document.getElementById("auth-toolbar");
  if (!toolbar) return;

  root.dataset.authToolbarState = show ? "visible" : "hidden";
  toolbar.dataset.authToolbarState = show ? "visible" : "hidden";
  root.style.setProperty("--auth-toolbar-height", show ? AUTH_TOOLBAR_HEIGHT : "0px");

  if (show) {
    toolbar.classList.remove("auth-toolbar--hidden");
    toolbar.setAttribute("aria-hidden", "false");
    toolbar.classList.add("auth-toolbar--reveal");
    window.setTimeout(() => toolbar.classList.remove("auth-toolbar--reveal"), 450);
  } else {
    toolbar.classList.remove("auth-toolbar--reveal");
    toolbar.classList.add("auth-toolbar--hidden");
    toolbar.setAttribute("aria-hidden", "true");
  }
}

/** Hide the site auth toolbar on scroll down; reveal on scroll up. */
export function useScrollHideSiteHeader(enabled = true) {
  const [visible, setVisible] = useState(true);
  const lastYRef = useRef(0);
  const tickingRef = useRef(false);
  const lockedRef = useRef(false);
  const armedRef = useRef(false);
  const visibleRef = useRef(true);

  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  const setVisibility = (next) => {
    if (next === visibleRef.current) return;

    lockedRef.current = true;
    lastYRef.current = window.scrollY;
    visibleRef.current = next;
    setVisible(next);

    window.setTimeout(() => {
      lockedRef.current = false;
      lastYRef.current = window.scrollY;
    }, TOGGLE_COOLDOWN_MS);
  };

  useEffect(() => {
    if (!enabled || prefersReducedMotion()) {
      armedRef.current = true;
      visibleRef.current = true;
      setVisible(true);
      return;
    }

    lastYRef.current = window.scrollY;
    const armTimer = window.setTimeout(() => {
      armedRef.current = true;
      lastYRef.current = window.scrollY;
    }, 150);

    const update = () => {
      tickingRef.current = false;
      if (!armedRef.current || lockedRef.current) return;

      const y = window.scrollY;
      const delta = y - lastYRef.current;

      if (y <= TOP_REVEAL_Y) {
        setVisibility(true);
      } else if (delta > SCROLL_DELTA_HIDE) {
        setVisibility(false);
      } else if (delta < -SCROLL_DELTA_SHOW) {
        setVisibility(true);
      }

      lastYRef.current = y;
    };

    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.clearTimeout(armTimer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [enabled]);

  useLayoutEffect(() => {
    const show = !enabled || visible || prefersReducedMotion();
    applySiteHeaderVisible(show);
  }, [visible, enabled]);

  useEffect(() => {
    return () => applySiteHeaderVisible(true);
  }, []);
}
